import { QueryClient, queryOptions, useQuery } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { deserializeUser } from '@teetsh/app/src/queries/user/serializer';
import { requireUserSession } from '@teetsh/app/src/queries/requireUserSession';
import { useQueryErrorNotification } from '@teetsh/app/src/containers/App/Notifications/useQueryErrorNotification';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';

export const USER_KEY = 'user';

function getUser() {
  return request('/user', {
    method: 'GET',
  }).then(res => deserializeUser(res));
}

function userQuery() {
  return queryOptions({
    queryKey: [USER_KEY],
    queryFn: getUser,
    retry: (failCount, err) => {
      if (err?.response?.status === 401) {
        return false;
      }
      return failCount < 1;
    },
  });
}

export const loader = (queryClient: QueryClient) => async () => {
  await requireUserSession();
  return queryClient.ensureQueryData(userQuery());
};

export default function useUser() {
  const { t } = useTranslation(['common']);
  const query = useQuery({
    ...userQuery(),
    refetchOnWindowFocus: false,

    // 30 mins
    staleTime: 30 * (60 * 1000),

    // 35 mins
    gcTime: 35 * (60 * 1000),
  });

  // We can't distinguish a ERR_CERT_AUTHORITY_INVALID from ERR_CONNECTION_REFUSED or any other ERR_NETWORK
  // So we will just adapt the message for old Macbook OS
  const isOldMacOS = (() => {
    const userAgent = navigator.userAgent;
    // El Capitan (10.11) or Yosemite (10.10)
    return /Mac OS X 10[_.]1[01]/.test(userAgent);
  })();

  useQueryErrorNotification({
    isError:
      query.isError &&
      query.error?.response?.status !== 401 &&
      query.error?.response?.status !== 503,
    message: isOldMacOS
      ? t('common:messages.oldMacOSNetworkError')
      : t('common:messages.genericErrorLong'),
    ctaButton: isOldMacOS
      ? {
          url: 'https://teetsh.notion.site/Pouvoir-se-connecter-malgr-probl-me-sur-les-vieux-Macbook-bdeded3ac50c48d2bfeba05c76f56b5a?source=copy_link',
          text: t('common:messages.findSolution'),
        }
      : undefined,
    timeout: 19000,
  });

  useEffect(() => {
    if (
      query.isError &&
      query.error?.response?.status !== 401 &&
      query.error?.response?.status !== 503
    ) {
      Sentry.captureException(query.error, {
        extra: {
          isOldMacOS,
        },
      });
    }
  }, [query.isError, query.error]);

  return query;
}
